import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { supabase } from '../../supabase';
import { aiSafeActionError } from '../../utils/error/aiErrorMessages';
import { randomUuid } from '../../utils/randomUuid';
import { usePriceVisibility } from '../usePriceVisibility';
import { fieldReadTablesForRole } from '../../utils/job/fieldReadTables';

/**
 * Pulls a structured `error.code` out of a Supabase functions.invoke result.
 * 4xx/5xx responses surface as `error.context` (a Response); body may also be in `data`.
 */
async function extractFnErrorCode(data, err) {
  if (data && typeof data === 'object' && data.error?.code) return data.error.code;
  if (err?.context && typeof err.context.json === 'function') {
    try {
      const body = await err.context.json();
      if (body?.error?.code) return body.error.code;
    } catch (_) { /* ignore parse failure */ }
  }
  return null;
}

/**
 * Handles send_job_summary action confirmation and post-log auto-append.
 * Extracted to keep useAiAssistant.js under the 500-line limit.
 *
 * @param {React.MutableRefObject<Array>} messagesRef — latest messages (avoids stale closure on confirm click)
 */
export default function useAiSendSummary({ role, messages, messagesRef, setMessages }) {
  const intl = useIntl();
  const { canSeePrices } = usePriceVisibility();
  // RD-ROLE-005b: send-summary is worker-only (every entry point below
  // gates `role !== 'worker'`), and a blind worker has no base `jobs`/
  // `customers` SELECT — route through the dollar-free field views instead.
  const fieldTables = fieldReadTablesForRole(role, canSeePrices);

  const confirmSendSummaryAction = useCallback(async (messageId) => {
    const list = messagesRef?.current ?? messages;
    const msg = list.find((m) => m.id === messageId);
    if (!msg?.action || msg.action.type !== 'send_job_summary' || msg.actionStatus !== 'pending') return;

    setMessages((prev) => prev.map((m) =>
      m.id === messageId ? { ...m, actionStatus: 'processing' } : m
    ));

    try {
      const jobId = msg.action?.job_id != null ? String(msg.action.job_id).trim() : '';
      if (!jobId) throw new Error('Missing job_id');

      if (role !== 'worker') throw new Error('Only workers can send job summaries from chat');

      const preview = msg.action.preview;
      let customer = null;

      if (
        preview &&
        typeof preview.email === 'string' &&
        preview.email.length > 0 &&
        typeof preview.comm_opt_out === 'boolean'
      ) {
        customer = { email: preview.email, comm_opt_out: preview.comm_opt_out };
      } else if (preview?.customer_id && String(preview.job_id || '') === jobId) {
        const { data: custRow, error: custErr } = await supabase
          .from(fieldTables.customers)
          .select('email, comm_opt_out')
          .eq('id', preview.customer_id)
          .maybeSingle();
        if (custErr || !custRow) {
          throw new Error('Job not found or access denied');
        }
        customer = custRow;
      } else {
        // Just-logged job may not be visible yet — retry briefly to absorb replication lag.
        let jobRow = null;
        for (let attempt = 0; attempt < 3; attempt++) {
          const { data: row } = await supabase
            .from(fieldTables.jobs)
            .select('id, customer_id')
            .eq('id', jobId)
            .maybeSingle();
          if (row?.id) { jobRow = row; break; }
          if (attempt < 2) await new Promise((r) => setTimeout(r, 300 * (attempt + 1)));
        }
        if (!jobRow?.id) {
          throw new Error('Job not found or access denied');
        }

        const { data: custRow, error: custErr } = await supabase
          .from(fieldTables.customers)
          .select('email, comm_opt_out')
          .eq('id', jobRow.customer_id)
          .maybeSingle();

        if (custErr || !custRow) {
          throw new Error('Customer record not accessible');
        }
        customer = custRow;
      }

      if (!customer?.email) throw new Error('Customer has no email on file');
      if (customer?.comm_opt_out) throw new Error('Customer has opted out of emails');

      // Retry up to 3x with backoff to absorb replication lag on a just-logged job.
      let data = null; let error = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        const res = await supabase.functions.invoke('ai-generate-job-summary', {
          body: { job_id: jobId, locale: intl.locale },
        });
        data = res.data; error = res.error;
        if (!error && data?.success) break;
        if (attempt < 2) await new Promise((r) => setTimeout(r, 300 * (attempt + 1)));
      }
      if (error || !data?.success) throw new Error(error?.message || 'Failed to send summary');

      setMessages((prev) => prev.map((m) =>
        m.id === messageId ? { ...m, actionStatus: 'completed' } : m
      ));
    } catch (err) {
      setMessages((prev) => prev.map((m) =>
        m.id === messageId
          ? { ...m, actionStatus: 'failed', actionResult: { error: aiSafeActionError(err) } }
          : m
      ));
    }
  }, [role, messages, messagesRef, setMessages, intl, fieldTables]);

  /**
   * Inline variant of the send-summary flow — mutates _summaryStatus on the
   * existing log_job completion message instead of appending a second card.
   * Keeps "Trabajo Registrado · Ver · Enviar resumen · Omitir" as a single
   * unified post-log surface (matches regular UI's PostJobAiModal pattern).
   */
  /**
   * @param {'generate_and_send'|'generate_only'} mode — matches PostJobAiModal (generate vs generate+email).
   */
  const sendSummaryInline = useCallback(async (logMessageId, jobId, jobPreview, mode = 'generate_and_send') => {
    if (!logMessageId || !jobId) return;
    if (role !== 'worker') return;
    const sendEmail = mode === 'generate_and_send';

    const existing = messagesRef?.current?.find((m) => m.id === logMessageId);
    if (existing?._summaryStatus === 'processing' || existing?._summaryStatus === 'completed') return;

    setMessages((prev) => prev.map((m) =>
      m.id === logMessageId
        ? { ...m, _summaryStatus: 'processing', _summaryError: null, _summaryLastMode: mode }
        : m,
    ));

    try {
      const pv = jobPreview;
      let customer = null;

      if (sendEmail) {
        if (
          pv &&
          typeof pv.email === 'string' && pv.email.length > 0 &&
          typeof pv.comm_opt_out === 'boolean'
        ) {
          customer = { email: pv.email, comm_opt_out: pv.comm_opt_out };
        } else if (pv?.customer_id) {
          const { data: custRow, error: custErr } = await supabase
            .from(fieldTables.customers)
            .select('email, comm_opt_out')
            .eq('id', pv.customer_id)
            .maybeSingle();
          if (custErr || !custRow) throw new Error('Customer record not accessible');
          customer = custRow;
        } else {
          throw new Error('Missing customer context');
        }
        if (!customer?.email?.trim()) throw new Error('Customer has no email on file');
        if (customer?.comm_opt_out) throw new Error('Customer has opted out of emails');
      }

      let data = null;
      let error = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        const res = await supabase.functions.invoke('ai-generate-job-summary', {
          body: { job_id: jobId, locale: intl.locale },
        });
        data = res.data;
        error = res.error;
        if (!error && data?.success) break;
        if (attempt < 2) await new Promise((r) => setTimeout(r, 300 * (attempt + 1)));
      }
      if (error || !data?.success) throw new Error(error?.message || 'Failed to generate summary');

      if (sendEmail) {
        const { data: sendData, error: sendErr } = await supabase.functions.invoke('send-job-summary-email', {
          body: { job_id: jobId },
        });
        // 409 DUPLICATE_SEND or 403 SUMMARY_OPT_OUT/COMM_OPT_OUT come back via FunctionsHttpError.
        // Body lives in sendErr.context (Response object) or in sendData on some versions.
        const errCode = await extractFnErrorCode(sendData, sendErr);
        if (errCode === 'DUPLICATE_SEND') {
          // Treat as "already sent" — completed-but-noop. User-facing UX: success outcome.
          setMessages((prev) => prev.map((m) =>
            m.id === logMessageId
              ? { ...m, _summaryStatus: 'completed', _summaryOutcome: 'already_sent' }
              : m,
          ));
          return;
        }
        if (errCode === 'SUMMARY_OPT_OUT' || errCode === 'COMM_OPT_OUT') {
          throw new Error(intl.formatMessage({ id: 'aiSummary.summaryOptOut.toast', defaultMessage: 'Customer has unsubscribed from service summaries.' }));
        }
        if (sendErr || !sendData?.success) {
          throw new Error(sendErr?.message || 'Failed to send summary email');
        }
      }

      setMessages((prev) => prev.map((m) =>
        m.id === logMessageId
          ? {
            ...m,
            _summaryStatus: 'completed',
            _summaryOutcome: sendEmail ? 'sent' : 'generated',
          }
          : m,
      ));
    } catch (err) {
      setMessages((prev) => prev.map((m) =>
        m.id === logMessageId
          ? { ...m, _summaryStatus: 'failed', _summaryError: aiSafeActionError(err) }
          : m,
      ));
    }
  }, [role, messagesRef, setMessages, intl, fieldTables]);

  const appendSendSummaryCard = useCallback((logMessageId, jobId, jobPreview) => {
    setMessages((prev) => {
      const source = prev.find((m) => m.id === logMessageId);
      if (!source || source._sendSummaryAppended) return prev;

      const action = {
        type: 'send_job_summary',
        job_id: jobId,
        ...(jobPreview && typeof jobPreview === 'object' ? { preview: jobPreview } : {}),
      };

      const summaryMsg = {
        id: randomUuid(),
        role: 'assistant',
        text: '',
        action,
        actionStatus: 'pending',
        actionResult: null,
        createdAt: Date.now(),
      };

      return [
        ...prev.map((m) => (m.id === logMessageId ? { ...m, _sendSummaryAppended: true } : m)),
        summaryMsg,
      ];
    });
  }, [setMessages]);

  const confirmBulkSendSummaryAction = useCallback(async (messageId, jobIds, flagKey = '_eodBulkSent') => {
    if (!jobIds?.length) return;

    if (role !== 'worker') return;

    const list = messagesRef?.current ?? messages;
    const existing = list.find((m) => m.id === messageId);
    if (existing?.[flagKey]) return;

    setMessages((prev) => prev.map((m) =>
      m.id === messageId ? { ...m, actionStatus: 'processing' } : m
    ));

    let sent = 0;
    let cached = 0;
    let failed = 0;
    const skipped = { noEmail: 0, optOut: 0, duplicate: 0 };

    for (const jobId of jobIds) {
      try {
        const { data: jobRow, error: jobErr } = await supabase
          .from(fieldTables.jobs)
          .select('id, customer_id')
          .eq('id', jobId)
          .maybeSingle();

        if (jobErr || !jobRow?.id) { failed++; continue; }

        const { data: customer, error: custErr } = await supabase
          .from(fieldTables.customers)
          .select('email, comm_opt_out, email_summaries_opt_out')
          .eq('id', jobRow.customer_id)
          .maybeSingle();

        if (custErr || !customer) { failed++; continue; }
        if (!customer?.email) { skipped.noEmail++; continue; }
        if (customer.comm_opt_out || customer.email_summaries_opt_out) { skipped.optOut++; continue; }

        // Generate (server-side cache reuse handles the dedup; no client-side window check needed)
        const { data, error } = await supabase.functions.invoke('ai-generate-job-summary', {
          body: { job_id: jobId, locale: intl.locale },
        });
        if (error || !data?.success) { failed++; continue; }
        if (data.cached === true) { cached++; } else { sent++; }
      } catch {
        failed++;
      }
    }

    setMessages((prev) => prev.map((m) =>
      m.id === messageId
        ? { ...m, actionStatus: 'completed', actionResult: { sent, cached, skipped, failed }, [flagKey]: true }
        : m
    ));
  }, [role, messages, messagesRef, setMessages, intl, fieldTables]);

  return { confirmSendSummaryAction, appendSendSummaryCard, sendSummaryInline, confirmBulkSendSummaryAction };
}
